'use client';

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  IconButton,
  Input,
  Popover,
  Portal,
  SimpleGrid,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  IconBuildingStore,
  IconChevronDown,
  IconLayoutGrid,
  IconSearch,
  IconStar,
  IconX,
} from '@tabler/icons-react';
import {
  type MouseEvent,
  type ReactNode,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getDataExplorerModuleMetadata,
  searchDataExplorerModules,
} from '@/lib/attune/data-explorer';
import { DataExplorerCard } from './DataExplorerCard';

export type DataExplorerItem = {
  href: string;
  label: string;
  description?: string;
  subtitle?: string;
  metrics?: string[];
  searchTerms?: readonly string[];
  section: string;
  icon?: ReactNode;
};

export type DataExplorerProps = {
  items: DataExplorerItem[];
  favoriteHrefs: readonly string[];
  favoriteItems?: DataExplorerItem[];
  favoritesReady?: boolean;
  onToggleFavorite: (href: string) => void;
  resolveHref: (href: string) => string;
  activeHref?: string;
  disabled?: boolean;
  onNavigate?: (href: string) => void;
  renderBrands?: (props: { query: string; onNavigate: (href: string) => void }) => ReactNode;
};

const EXAMPLE_QUERIES = ['Share of voice', 'Rage clicks', 'AI crawler traffic'];
const FOCUS_STYLE = {
  outline: '2px solid',
  outlineColor: 'accent.focusRing',
  outlineOffset: '2px',
};

export function DataExplorer({
  items,
  favoriteHrefs,
  favoriteItems = [],
  favoritesReady = true,
  onToggleFavorite,
  resolveHref,
  activeHref,
  disabled = false,
  onNavigate,
  renderBrands,
}: DataExplorerProps) {
  const isDesktop = useBreakpointValue({ base: false, lg: true }) ?? false;
  const Explorer = isDesktop ? Popover : Dialog;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownQuery, setDropdownQuery] = useState('');
  const dropdownSearchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const searchPending = deferredQuery !== query;
  const [category, setCategory] = useState('All modules');
  const [view, setView] = useState<'modules' | 'brands' | 'favorites'>('modules');
  const favoritesOnly = view === 'favorites';
  const searchRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const allItems = useMemo(
    () => [...new Map([...items, ...favoriteItems].map(item => [item.href, item])).values()],
    [items, favoriteItems]
  );
  const searchableItems = isDesktop || favoritesOnly ? allItems : items;
  const categories = [...new Set(searchableItems.map(item => item.section))];
  const favorites = new Set(favoriteHrefs);
  const dropdownResults = searchDataExplorerModules(items, dropdownQuery);
  const dropdownGroups = [...new Set(items.map(item => item.section))]
    .map(title => ({
      title,
      results: dropdownResults.filter(result => result.item.section === title),
    }))
    .filter(group => group.results.length);
  const favoriteCount = allItems.filter(item => favorites.has(item.href)).length;
  const searchResults = useMemo(
    () => searchDataExplorerModules(searchableItems, deferredQuery),
    [searchableItems, deferredQuery]
  );
  const inView = searchResults.filter(result => !favoritesOnly || favorites.has(result.item.href));
  const results = inView.filter(
    result => category === 'All modules' || result.item.section === category
  );
  const isSearching = Boolean(query.trim());
  useEffect(() => {
    if (resultsRef.current) resultsRef.current.scrollTop = 0;
  }, [query, category, view]);
  useEffect(() => {
    setOpen(false);
    setDropdownOpen(false);
  }, [isDesktop]);
  const groups = isSearching
    ? [{ title: 'Search results', results }]
    : categories
        .map(title => ({ title, results: results.filter(result => result.item.section === title) }))
        .filter(group => group.results.length);

  function navigate(href: string, event?: MouseEvent<HTMLAnchorElement>) {
    if (
      event &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    )
      return;
    if (onNavigate) {
      event?.preventDefault();
      onNavigate(resolveHref(href));
    } else if (!event) {
      // Activate the real link so keyboard selection uses the same navigation behavior.
      resultsRef.current?.querySelector<HTMLAnchorElement>('a[href]')?.click();
    }
    setOpen(false);
  }

  function resetFilters() {
    setQuery('');
    setCategory('All modules');
    setView('modules');
    searchRef.current?.focus();
  }

  function renderMenuLink(item: DataExplorerItem) {
    const favorite = favorites.has(item.href);
    return (
      <Flex
        key={item.href}
        align="center"
        minW={0}
        w="full"
        borderRadius="4px"
        bg={activeHref === item.href ? 'ui.surface.selected' : undefined}
        css={{
          '& .menu-favorite': { opacity: favorite ? 1 : 0 },
          '&:hover .menu-favorite, &:focus-within .menu-favorite': { opacity: 1 },
          '@media (hover: none)': { '& .menu-favorite': { opacity: 1 } },
        }}
        _hover={{ bg: 'ui.surface.rowHover' }}
      >
        <Button
          asChild
          variant="plain"
          size="xs"
          h="28px"
          minH="28px"
          gap="2"
          flex="1"
          minW={0}
          px="2"
          justifyContent="flex-start"
          fontWeight={activeHref === item.href ? '500' : '400'}
          _focusVisible={FOCUS_STYLE}
        >
          <a
            href={resolveHref(item.href)}
            aria-current={activeHref === item.href ? 'page' : undefined}
            onClick={event => navigate(item.href, event)}
          >
            <Box
              flexShrink={0}
              boxSize="16px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="ui.text.tertiary"
              css={{ '& svg': { width: '15px', height: '15px' } }}
            >
              {item.icon}
            </Box>
            <Text as="span" truncate fontSize="12px">
              {item.label}
            </Text>
          </a>
        </Button>
        <IconButton
          className="menu-favorite"
          aria-label={`${favorite ? 'Remove' : 'Add'} ${item.label} ${favorite ? 'from' : 'to'} favorites`}
          aria-pressed={favorite}
          disabled={!favoritesReady}
          variant="ghost"
          size="xs"
          h="26px"
          minW="26px"
          w="26px"
          flexShrink={0}
          color="ui.text.tertiary"
          onClick={() => onToggleFavorite(item.href)}
          _focusVisible={FOCUS_STYLE}
        >
          <IconStar size={11} fill={favorite ? 'currentColor' : 'none'} />
        </IconButton>
      </Flex>
    );
  }

  return (
    <Explorer.Root
      lazyMount
      unmountOnExit
      open={open && !disabled}
      onOpenChange={details => {
        setOpen(details.open);
        if (details.open) {
          setDropdownOpen(false);
          setDropdownQuery('');
          setQuery('');
          setCategory('All modules');
          setView(isDesktop ? 'modules' : 'favorites');
        }
      }}
      initialFocusEl={() => searchRef.current}
      {...(isDesktop
        ? {
            modal: false,
            closeOnEscape: true,
            closeOnInteractOutside: true,
            positioning: {
              placement: 'bottom-start' as const,
              strategy: 'fixed' as const,
              gutter: 8,
              flip: true,
              slide: true,
            },
          }
        : { placement: 'center' as const, scrollBehavior: 'inside' as const })}
    >
      {isDesktop ? (
        <Popover.Trigger asChild>
          <Button
            aria-label="Open Data Explorer"
            disabled={disabled}
            variant="ghost"
            size="sm"
            h="32px"
            px="2.5"
            gap="2"
            borderRadius="6px"
            borderWidth="1px"
            borderColor="ui.border.subtle"
            bg="ui.surface.card"
            color="ui.text.secondary"
            flexShrink={0}
            _hover={{
              bg: 'ui.surface.input',
              color: 'ui.text.primary',
              borderColor: 'ui.border.default',
            }}
            _focusVisible={FOCUS_STYLE}
          >
            <IconSearch size={15} stroke={1.6} aria-hidden="true" />
            <Text
              as="span"
              display={{ base: 'none', md: 'inline' }}
              fontSize="13px"
              fontWeight="500"
              letterSpacing="-0.015em"
            >
              Data Explorer
            </Text>
            <IconChevronDown
              size={14}
              aria-hidden="true"
              style={{ transform: open ? 'rotate(180deg)' : undefined }}
            />
          </Button>
        </Popover.Trigger>
      ) : (
        <Flex align="center" flexShrink={0}>
          <Dialog.Trigger asChild>
            <Button
              aria-label="Open Data Explorer"
              disabled={disabled}
              variant="ghost"
              size="sm"
              h="32px"
              px="2.5"
              gap="2"
              borderRadius="6px 0 0 6px"
              borderWidth="1px"
              borderColor="ui.border.subtle"
              bg="ui.surface.card"
              color="ui.text.secondary"
              flexShrink={0}
              _hover={{
                bg: 'ui.surface.input',
                color: 'ui.text.primary',
                borderColor: 'ui.border.default',
              }}
              _focusVisible={FOCUS_STYLE}
            >
              <IconSearch size={15} stroke={1.6} aria-hidden="true" />
              <Text
                as="span"
                display={{ base: 'none', md: 'inline' }}
                fontSize="13px"
                fontWeight="500"
                letterSpacing="-0.015em"
              >
                Data Explorer
              </Text>
            </Button>
          </Dialog.Trigger>
          <Popover.Root
            lazyMount
            unmountOnExit
            open={dropdownOpen && !disabled}
            onOpenChange={details => {
              setDropdownOpen(details.open);
              if (details.open) setOpen(false);
              else setDropdownQuery('');
            }}
            initialFocusEl={() => dropdownSearchRef.current}
            positioning={{
              placement: 'bottom-end',
              strategy: 'fixed',
              gutter: 6,
              flip: true,
              slide: true,
            }}
          >
            <Popover.Trigger asChild>
              <IconButton
                aria-label="Browse Data Explorer pages"
                disabled={disabled}
                variant="ghost"
                size="sm"
                h="32px"
                minW="28px"
                px="1"
                borderRadius="0 6px 6px 0"
                borderWidth="1px"
                borderLeftWidth="0"
                borderColor="ui.border.subtle"
                bg="ui.surface.card"
                color="ui.text.secondary"
                _hover={{ bg: 'ui.surface.input', color: 'ui.text.primary' }}
                _focusVisible={FOCUS_STYLE}
              >
                <IconChevronDown
                  size={14}
                  aria-hidden="true"
                  style={{ transform: dropdownOpen ? 'rotate(180deg)' : undefined }}
                />
              </IconButton>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner zIndex="dropdown">
                <Popover.Content
                  aria-label="Product navigation"
                  layerStyle="surfacePopover"
                  w="320px"
                  maxW="calc(100vw - 24px)"
                  maxH="min(var(--available-height), 520px)"
                  overflow="hidden"
                  borderRadius="14px"
                  bg="ui.surface.card"
                  p="2"
                >
                  <Input
                    ref={dropdownSearchRef}
                    aria-label="Search pages"
                    placeholder="Search pages…"
                    flexShrink={0}
                    value={dropdownQuery}
                    onChange={event => setDropdownQuery(event.target.value)}
                    size="sm"
                    mb="2"
                  />
                  <Box overflowY="auto" minH={0}>
                    {dropdownGroups.map(({ title: section, results: matches }) => {
                      return (
                        <Box key={section} mb="2">
                          <Text
                            fontSize="xs"
                            fontWeight="600"
                            color="ui.text.tertiary"
                            px="2"
                            py="1"
                          >
                            {section}
                          </Text>
                          {matches.map(({ item }) => (
                            <Flex key={item.href} align="center" gap="1">
                              <Button
                                asChild
                                variant="ghost"
                                size="sm"
                                flex="1"
                                minW={0}
                                justifyContent="flex-start"
                                bg={activeHref === item.href ? 'ui.surface.selected' : undefined}
                                _focusVisible={FOCUS_STYLE}
                              >
                                <a
                                  href={resolveHref(item.href)}
                                  aria-current={activeHref === item.href ? 'page' : undefined}
                                  onClick={event => {
                                    navigate(item.href, event);
                                    setDropdownOpen(false);
                                    setDropdownQuery('');
                                  }}
                                >
                                  {item.icon}
                                  <Text as="span" truncate>
                                    {item.label}
                                  </Text>
                                </a>
                              </Button>
                              <IconButton
                                aria-label={`${favorites.has(item.href) ? 'Remove' : 'Add'} ${item.label} ${favorites.has(item.href) ? 'from' : 'to'} favorites`}
                                aria-pressed={favorites.has(item.href)}
                                disabled={!favoritesReady}
                                variant="ghost"
                                size="xs"
                                onClick={() => onToggleFavorite(item.href)}
                                _focusVisible={FOCUS_STYLE}
                              >
                                <IconStar
                                  size={14}
                                  fill={favorites.has(item.href) ? 'currentColor' : 'none'}
                                />
                              </IconButton>
                            </Flex>
                          ))}
                        </Box>
                      );
                    })}
                    {!dropdownResults.length ? (
                      <Text p="3" fontSize="sm" color="ui.text.secondary">
                        No matching pages
                      </Text>
                    ) : null}
                  </Box>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        </Flex>
      )}
      <Portal>
        {!isDesktop ? (
          <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(3px)" />
        ) : null}
        <Explorer.Positioner
          zIndex={isDesktop ? 'popover' : 'modal'}
          p={isDesktop ? undefined : { base: '3', md: '6' }}
        >
          <Explorer.Content
            data-section="dataExplorer"
            aria-label="Data Explorer"
            w={isDesktop ? '960px' : '1000px'}
            maxW="calc(100vw - 24px)"
            h={isDesktop ? 'auto' : 'min(780px, calc(100dvh - 48px))'}
            maxH={
              isDesktop ? 'var(--available-height, calc(100dvh - 80px))' : 'calc(100dvh - 24px)'
            }
            borderRadius="12px"
            borderWidth="1px"
            borderColor="ui.border.default"
            bg="ui.surface.card"
            color="ui.text.primary"
            boxShadow="var(--attune-shadow-floating)"
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            {isDesktop ? (
              <>
                <Flex
                  p="3"
                  gap="2"
                  align="center"
                  borderBottomWidth="1px"
                  borderColor="ui.border.subtle"
                  flexShrink={0}
                >
                  <Box color="ui.text.tertiary">
                    <IconSearch size={17} aria-hidden="true" />
                  </Box>
                  <Input
                    ref={searchRef}
                    aria-label="Search pages"
                    placeholder="Search pages or metrics…"
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    onKeyDown={event => {
                      if (
                        event.nativeEvent.isComposing ||
                        searchPending ||
                        resultsRef.current?.querySelector('[aria-busy="true"]')
                      )
                        return;
                      const first = resultsRef.current?.querySelector<HTMLAnchorElement>('a[href]');
                      if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        first?.focus();
                      }
                      if (event.key === 'Enter' && isSearching) {
                        event.preventDefault();
                        first?.click();
                      }
                    }}
                    variant="flushed"
                    h="30px"
                    minH="30px"
                    fontSize="sm"
                    _focusVisible={{
                      outline: 'none',
                      boxShadow: 'inset 0 -2px 0 var(--chakra-colors-accent-focus-ring)',
                    }}
                    size="sm"
                    border="0"
                    flex="1"
                    minW={0}
                  />
                  <Button asChild size="xs" variant="ghost" _focusVisible={FOCUS_STYLE}>
                    <a
                      href={resolveHref('/app/ai-search/competitors')}
                      onClick={event => navigate('/app/ai-search/competitors', event)}
                    >
                      Browse brands
                    </a>
                  </Button>
                  <Popover.CloseTrigger asChild>
                    <CloseButton size="xs" aria-label="Close Data Explorer" />
                  </Popover.CloseTrigger>
                </Flex>
                <Box
                  ref={resultsRef}
                  onKeyDown={event => {
                    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
                    const links = Array.from(
                      event.currentTarget.querySelectorAll<HTMLAnchorElement>('a[href]')
                    );
                    const index = links.indexOf(event.target as HTMLAnchorElement);
                    if (index < 0) return;
                    event.preventDefault();
                    const next = index + (event.key === 'ArrowDown' ? 1 : -1);
                    if (next < 0) searchRef.current?.focus();
                    else links[Math.min(next, links.length - 1)]?.focus();
                  }}
                  overflowY="auto"
                  minH={0}
                  p="3"
                  overscrollBehavior="contain"
                  aria-busy={searchPending}
                  onClickCapture={event => {
                    if (searchPending) {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  }}
                >
                  <>
                    {!results.length ? (
                      <Text role="status" p="4" fontSize="sm" color="ui.text.secondary">
                        No matching pages
                      </Text>
                    ) : null}
                    {isSearching ? (
                      <SimpleGrid columns={{ base: 2, lg: 4 }} gap="1">
                        {results.map(({ item }) => renderMenuLink(item))}
                      </SimpleGrid>
                    ) : (
                      <Box css={{ columnCount: { base: 2, lg: 4 }, columnGap: '16px' }}>
                        {categories
                          .map(title => ({
                            title,
                            results: results.filter(result => result.item.section === title),
                          }))
                          .filter(group => group.results.length)
                          .map(group => (
                            <Box key={group.title} minW={0} mb="3" css={{ breakInside: 'avoid' }}>
                              <Text
                                as="h3"
                                fontSize="11px"
                                fontWeight="600"
                                color="ui.text.secondary"
                                mb="1"
                                mx="2"
                                pb="1.5"
                                borderBottomWidth="1px"
                                borderColor="ui.border.subtle"
                              >
                                {group.title}
                              </Text>
                              <Flex direction="column" gap="0">
                                {group.results.map(({ item }) => renderMenuLink(item))}
                              </Flex>
                            </Box>
                          ))}
                      </Box>
                    )}
                  </>
                </Box>
              </>
            ) : (
              <>
                <Explorer.Header
                  px={{ base: '4', md: '3' }}
                  pt="5"
                  pb="4"
                  display="block"
                  flexShrink={0}
                >
                  <Explorer.Title fontSize="lg" fontWeight="600" letterSpacing="-0.025em">
                    Data Explorer
                  </Explorer.Title>
                  <Explorer.Description mt="1" color="ui.text.secondary" fontSize="sm" pr="7">
                    Explore your market. Find a brand, a metric, or your next insight.
                  </Explorer.Description>
                  <Explorer.CloseTrigger asChild position="absolute" top="4" right="4">
                    <CloseButton size="sm" aria-label="Close Data Explorer" />
                  </Explorer.CloseTrigger>
                  <Flex
                    mt="3"
                    align="center"
                    gap="3"
                    px="3.5"
                    bg="ui.surface.input"
                    borderWidth="1px"
                    borderColor="ui.border.subtle"
                    borderRadius="7px"
                    _focusWithin={{ borderColor: 'accent.focusRing' }}
                  >
                    <IconSearch size={21} stroke={1.6} aria-hidden="true" />
                    <Input
                      ref={searchRef}
                      value={query}
                      onChange={event => setQuery(event.target.value)}
                      onKeyDown={event => {
                        if (event.nativeEvent.isComposing) return;
                        if (
                          searchPending ||
                          resultsRef.current?.querySelector('[aria-busy="true"]')
                        )
                          return;
                        if (event.key === 'Enter' && isSearching) {
                          event.preventDefault();
                          resultsRef.current?.querySelector<HTMLAnchorElement>('a[href]')?.click();
                        }
                        if (event.key === 'ArrowDown') {
                          event.preventDefault();
                          resultsRef.current?.querySelector<HTMLAnchorElement>('a[href]')?.focus();
                        }
                      }}
                      aria-label={
                        view === 'brands' ? 'Search brands' : 'Search metrics and modules'
                      }
                      placeholder={
                        view === 'brands'
                          ? 'Search brands or websites…'
                          : 'What would you like to explore?'
                      }
                      autoComplete="off"
                      h="44px"
                      px="0"
                      minW={0}
                      flex="1"
                      fontSize="md"
                      bg="transparent"
                      border="0"
                      boxShadow="none"
                      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                    />
                    {query ? (
                      <IconButton
                        aria-label="Clear search"
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setQuery('');
                          searchRef.current?.focus();
                        }}
                      >
                        <IconX size={16} />
                      </IconButton>
                    ) : null}
                  </Flex>
                </Explorer.Header>

                <Flex
                  px={{ base: '4', md: '3' }}
                  gap="5"
                  borderBottomWidth="1px"
                  borderColor="ui.border.subtle"
                  flexShrink={0}
                >
                  {[
                    {
                      label: 'Favorites',
                      count: favoriteCount,
                      id: 'favorites' as const,
                      icon: IconStar,
                    },
                    {
                      label: 'Modules',
                      count: items.length,
                      id: 'modules' as const,
                      icon: IconLayoutGrid,
                    },
                    ...(renderBrands
                      ? [
                          {
                            label: 'Brands',
                            count: undefined,
                            id: 'brands' as const,
                            icon: IconBuildingStore,
                          },
                        ]
                      : []),
                  ].map(tab => (
                    <Button
                      key={tab.label}
                      variant="ghost"
                      aria-pressed={view === tab.id}
                      onClick={() => {
                        setView(tab.id);
                        setCategory('All modules');
                      }}
                      px="0"
                      h="44px"
                      gap="2"
                      borderRadius="0"
                      borderWidth="0"
                      borderBottomWidth="2px"
                      borderColor={view === tab.id ? 'accent.solid' : 'transparent'}
                      color={view === tab.id ? 'ui.text.primary' : 'ui.text.tertiary'}
                      _hover={{ color: 'ui.text.primary', bg: 'transparent' }}
                      _focusVisible={FOCUS_STYLE}
                    >
                      <tab.icon size={16} aria-hidden="true" />
                      {tab.label}
                      <Text as="span" fontSize="xs" fontWeight="400" color="ui.text.tertiary">
                        {tab.count}
                      </Text>
                    </Button>
                  ))}
                </Flex>

                <Flex flex="1" minH={0} direction={{ base: 'column', md: 'row' }}>
                  {view !== 'brands' ? (
                    <Flex
                      as="nav"
                      aria-label="Module categories"
                      direction={{ base: 'row', md: 'column' }}
                      w={{ base: 'full', md: '150px' }}
                      px={{ base: '3', md: '2' }}
                      py={{ base: '2', md: '2' }}
                      gap="1"
                      flexShrink={0}
                      overflowX="auto"
                      overflowY={{ base: 'hidden', md: 'auto' }}
                      bg="ui.surface.panel"
                      borderRightWidth={{ base: '0', md: '1px' }}
                      borderBottomWidth={{ base: '1px', md: '0' }}
                      borderColor="ui.border.subtle"
                    >
                      <Text
                        display={{ base: 'none', md: 'block' }}
                        px="3"
                        pb="2"
                        pt="1"
                        fontSize="xs"
                        color="ui.text.tertiary"
                      >
                        Explore by category
                      </Text>
                      {['All modules', ...categories].map(title => {
                        const count =
                          title === 'All modules'
                            ? inView.length
                            : inView.filter(result => result.item.section === title).length;
                        return (
                          <Button
                            key={title}
                            variant="ghost"
                            aria-pressed={category === title}
                            onClick={() => setCategory(title)}
                            size="sm"
                            px="3"
                            justifyContent="space-between"
                            gap="3"
                            flexShrink={0}
                            fontWeight={category === title ? '600' : '400'}
                            color={category === title ? 'ui.text.primary' : 'ui.text.secondary'}
                            bg={category === title ? 'ui.surface.selected' : 'transparent'}
                            borderRadius="8px"
                            _focusVisible={FOCUS_STYLE}
                          >
                            {title}
                            <Text as="span" fontSize="xs" color="ui.text.tertiary">
                              {count}
                            </Text>
                          </Button>
                        );
                      })}
                    </Flex>
                  ) : null}

                  <Box
                    ref={resultsRef}
                    aria-busy={searchPending}
                    onClickCapture={event => {
                      if (searchPending) {
                        event.preventDefault();
                        event.stopPropagation();
                      }
                    }}
                    flex="1"
                    minW={0}
                    overflowY="auto"
                    overscrollBehavior="contain"
                    p={{ base: '4', md: '3' }}
                  >
                    {view === 'brands' && renderBrands ? (
                      renderBrands({
                        query: deferredQuery,
                        onNavigate: href => navigate(href),
                      })
                    ) : (
                      <>
                        <Text
                          role="status"
                          aria-live="polite"
                          fontSize="xs"
                          color="ui.text.tertiary"
                          mb="4"
                        >
                          {results.length} {results.length === 1 ? 'module' : 'modules'}
                          {isSearching
                            ? ` matching “${deferredQuery.trim()}”`
                            : favoritesOnly
                              ? ' in your favorites'
                              : ' to explore'}
                        </Text>
                        {!results.length ? (
                          <Flex
                            direction="column"
                            align="center"
                            textAlign="center"
                            px="3"
                            py={{ base: '6', md: '12' }}
                            gap="3"
                          >
                            <Box color="ui.text.tertiary">
                              {favoritesOnly && !favoriteCount ? (
                                <IconStar size={30} stroke={1.3} />
                              ) : (
                                <IconSearch size={30} stroke={1.3} />
                              )}
                            </Box>
                            <Text fontWeight="600" fontSize="lg">
                              {favoritesOnly && !favoriteCount
                                ? 'Keep your go-to modules close'
                                : 'No matching modules'}
                            </Text>
                            <Text color="ui.text.secondary" fontSize="sm" maxW="320px">
                              {favoritesOnly && !favoriteCount
                                ? 'Star any module to build your own collection here.'
                                : 'Try a metric like “rage clicks”, or explore another category.'}
                            </Text>
                            <Button variant="outline" size="sm" mt="2" onClick={resetFilters}>
                              Browse all modules
                            </Button>
                          </Flex>
                        ) : (
                          groups.map(group => (
                            <Box key={group.title} mb="4">
                              <Flex align="center" gap="2.5" mb="3">
                                <Text as="h3" fontSize="sm" fontWeight="600">
                                  {group.title}
                                </Text>
                                <Text fontSize="xs" color="ui.text.tertiary">
                                  {group.results.length}
                                </Text>
                                <Box flex="1" h="1px" bg="ui.border.subtle" ml="1" />
                              </Flex>
                              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="2.5">
                                {group.results.map(({ item, matchedMetrics }) => {
                                  const metadata = getDataExplorerModuleMetadata(item.href);
                                  const metrics = [
                                    ...new Set([
                                      ...matchedMetrics,
                                      ...(item.metrics || metadata.metrics),
                                    ]),
                                  ].slice(0, 3);
                                  const isFavorite = favorites.has(item.href);
                                  const isActive = activeHref === item.href;
                                  return (
                                    <DataExplorerCard
                                      key={item.href}
                                      href={resolveHref(item.href)}
                                      title={item.label}
                                      subtitle={item.subtitle}
                                      description={
                                        item.description || metadata.capabilities[0] || ''
                                      }
                                      icon={item.icon}
                                      details={(metrics.length
                                        ? metrics
                                        : metadata.capabilities.slice(0, 2)
                                      ).map(label => ({
                                        label,
                                        highlighted: matchedMetrics.includes(label),
                                      }))}
                                      favorite={isFavorite}
                                      favoritesReady={favoritesReady}
                                      active={isActive}
                                      onNavigate={event => navigate(item.href, event)}
                                      onToggleFavorite={() => onToggleFavorite(item.href)}
                                    />
                                  );
                                })}
                              </SimpleGrid>
                            </Box>
                          ))
                        )}
                        {!isSearching && !favoritesOnly ? (
                          <Flex wrap="wrap" align="center" gap="2" pt="1" pb="2">
                            <Text fontSize="xs" color="ui.text.tertiary">
                              Try searching
                            </Text>
                            {EXAMPLE_QUERIES.map(example => (
                              <Button
                                key={example}
                                variant="plain"
                                size="xs"
                                color="ui.text.secondary"
                                textDecoration="underline"
                                textUnderlineOffset="3px"
                                onClick={() => {
                                  setQuery(example);
                                  setCategory('All modules');
                                  searchRef.current?.focus();
                                }}
                              >
                                {example}
                              </Button>
                            ))}
                          </Flex>
                        ) : null}
                      </>
                    )}
                  </Box>
                </Flex>
              </>
            )}
          </Explorer.Content>
        </Explorer.Positioner>
      </Portal>
    </Explorer.Root>
  );
}
